import { DEFAULT_LANDING_PAGE_ACCENT_COLOR } from '@consts';
import { GoalLandingPageMentorModel } from '@models/goals';
import { setupForDark } from '@theme';
import classNames from 'classnames';
import { useMemo } from 'react';
import AnimatedSection from '../../animations/AnimatedSection';
import { usePaymentAndOrderOptional } from '../../payments/PaymentAndOrderContext';
import { parseHighlightedText } from './highlighters';
import MentorBioCard from './MentorBioCard';
import { useLandingPageTranslation } from './useLandingPageTranslation';

interface GoalLandingPageMentorBioSectionProps {
  mentors?: GoalLandingPageMentorModel[];
  accentColor?: string;
  heading?: { title: string; introduction: string };
}

export default function GoalLandingPageMentorBioSection({
  mentors: mentorsProp,
  accentColor: accentColorProp,
  heading
}: GoalLandingPageMentorBioSectionProps = {}) {
  const ctx = usePaymentAndOrderOptional();
  const goal = ctx?.state.goal;
  const { t } = useLandingPageTranslation();
  const mentors = useMemo(() => mentorsProp ?? goal?.landingPage?.mentors, [mentorsProp, goal]);
  const mentorSection = useMemo(() => goal?.landingPage?.mentorSection, [goal]);
  const gender = useMemo(() => {
    if (mentors?.length === 1 && mentors[0]?.gender) {
      return mentors[0].gender.toLowerCase();
    }
    return 'many';
  }, [mentors]);
  const accentColor =
    accentColorProp ?? goal?.landingPage?.accentColor ?? DEFAULT_LANDING_PAGE_ACCENT_COLOR;

  if (!mentors || mentors.length === 0) return <></>;

  return (
    <div
      id='mentor'
      className={classNames(
        'w-full max-w-[1200px] mx-auto flex flex-col gap-huge pt-[64px]',
        'pb-xl @[640px]:pb-huge',
        'px-sm'
      )}
    >
      {/* Heading */}
      {heading ? (
        <div className='flex flex-col gap-md items-center'>
          <h2
            className={classNames(
              'font-bold leading-[1.1] text-center',
              'text-[32px] @[480px]:text-[40px] @[640px]:text-[60px] break-words'
            )}
            style={{ color: accentColor }}
          >
            {heading.title}
          </h2>
          <p
            className={classNames(
              'font-normal leading-[1.3] text-center max-w-[768px]',
              setupForDark('text-white'),
              'text-[16px] @[640px]:text-[24px]'
            )}
          >
            {heading.introduction}
          </p>
        </div>
      ) : (
        <div className='flex flex-col gap-md items-center'>
          <div className='flex flex-col font-bold leading-[1.1] text-center'>
            <p
              className={classNames(
                'flex flex-col @[640px]:flex-row gap-none @[640px]:gap-md',
                'text-[32px] @[480px]:text-[40px] @[640px]:text-[60px] break-words'
              )}
            >
              {mentorSection?.sectionTitle ? (
                <>
                  <span style={{ color: accentColor }}>{mentorSection.sectionTitle}</span>
                  {mentorSection.sectionLeader && (
                    <span className={classNames(setupForDark('text-white'))}>
                      {parseHighlightedText(
                        mentorSection.sectionLeader,
                        'text-white',
                        undefined,
                        accentColor
                      )}
                    </span>
                  )}
                </>
              ) : (
                <>
                  <span style={{ color: accentColor }}>
                    {t(`domains.goals.landingPage.mentor.${gender}.title`)}
                  </span>
                  <span style={{ color: accentColor }}>
                    {t(`domains.goals.landingPage.mentor.${gender}.leader`)}
                  </span>
                </>
              )}
            </p>
          </div>
          <div className='flex flex-col items-center max-w-[768px]'>
            <div
              className={classNames(
                'flex flex-col font-normal leading-[1.3] text-center whitespace-pre-wrap',
                'text-[16px] @[640px]:text-[24px]',
                setupForDark('text-white')
              )}
            >
              {parseHighlightedText(
                mentorSection?.introduction ||
                  t(`domains.goals.landingPage.mentor.${gender}.introduction`),
                'text-white',
                undefined,
                accentColor
              )}
            </div>
          </div>
        </div>
      )}

      {mentors.map((mentor, index) => (
        <AnimatedSection key={`${mentor.name}-${index}`}>
          <MentorBioCard mentor={mentor} accentColor={accentColor} />
        </AnimatedSection>
      ))}
    </div>
  );
}
